export type Lang = "en" | "bn";

export const translations = {
  en: {
    appTitle: "MyAquaFood",
    welcome: "Welcome to MyAquaFood",
    description: "Track your fish consumption and nutritional intake easily.",
    confirmBackToHome: "Are you sure you want to go back to home?",
    confirmBackToHome_HaveUnsavedChanges:
      "You have unsaved changes. Do you really want to go back to home?",
    surveyMode: "Survey Mode",
    userMode: "User Mode",
    profile: "Profile",
    profileNotFound: "Profile not found",
    editProfile: "Edit Profile",
    updateProfile: "Update Profile",
    profileUpdated: "Profile updated successfully.",
    respondentDeleted: "Respondent profile deleted successfully.",
    deleteAccount: "Delete Account",
    deleteAccountTitle: "Delete Account",
    deleteAccountWarning: "Are you sure you want to delete your account? This action cannot be undone.",
    deleteAccountImplications: "Deleting your account will permanently remove all your data, including:",
    deleteAccountImplication1: "All your fish consumption logs and history",
    deleteAccountImplication2: "Your personal profile information",
    deleteAccountImplication3: "All nutritional calculations and statistics",
    deleteAccountImplication4: "Your account credentials and access",
    deleteAccountConfirmPassword: "Enter your password to confirm deletion",
    deleteAccountPasswordPlaceholder: "Enter your password",
    deleteAccountConfirmButton: "Delete My Account",
    deleteAccountCancelButton: "Cancel",
    deleteAccountSuccess: "Your account has been deleted successfully.",
    deleteAccountError: "Failed to delete account. Please try again.",
    deleteAccountWrongPassword: "Incorrect password. Please try again.",
    profileOptions: "Profile Options",
    loading: "Loading...",
    invalidID: "Invalid ID",
    surveyModeDescription:
      "In this mode, you can record fish consumption data on behalf of others, such as survey respondents. Each person will have their own record containing daily meal logs and nutritional calculations.",
    // userModeDescription:
    //   "Track your own daily fish consumption. Log meals, select fish species, and see how much nutrition you are getting from your diet.",
    userModeDescription: "Track your own daily fish consumption.",

    addNewPerson: "New Respondent",
    addNewPersonShort: "New Respondent",
    id: "ID",
    name: "Full Name or ID",
    age: "Age",
    sex: "Sex",
    selectSex: "Select Sex",
    male: "Male",
    female: "Female",
    other: "Other",
    prefer_not_to_say: "Prefer not to say",
    not_specified: "Not Specified",
    mobile: "Contact",
    occupation: "Occupation",
    selectOccupation: "Select Occupation",
    monthlyIncome: "Monthly Income",
    farmer: "Farmer",
    fisherman: "Fisherman",
    government_officer: "Government Officer",
    government_employee: "Government Employee",
    private_service: "Private Service",
    business: "Business",
    housewife: "Housewife",
    student: "Student",
    teacher: "Teacher",
    address: "Address",
    district: "District",
    upazila: "Upazila",
    union: "Union",
    village: "Village",
    notes: "Notes (optional)",
    save: "✅ Save Person",
    saveAndStart: "🚀 Start Survey",
    SaveAndSkipSurvey: "Save and Skip Survey",
    delete: "Delete",
    back: "Back",
    requiredFields: "Please fill in all required fields.",
    invalidAge: "Please enter a valid age between {min} and {max}.",
    personWithMobileExists: "A person with this contact already exists.",
    setAvatar: "Set Avatar",
    changeAvatar: "Change Avatar",

    surveyedPersonsList: "Respondents List",
    yourSurveyedPersons: "Your Surveyed Persons",
    viewAllPersons: "View All Respondents",
    noRespondents: "No respondents have been added yet.",
    confirmDeleteRespondent:
      "Are you sure you want to delete this respondent? This action cannot be undone.",

    logDay: "New log",
    allLogs: "All Logs",

    didYouEatToday: "Did you consume fish today?",
    didYouEatYesterday: "Did you consume fish yesterday?",
    whenLastAte: "When have you last consumed fish?",
    yes: "Yes",
    no: "No",
    closeSurvey: "Close Survey",
    close: "Close",
    cancel: "Cancel",

    startNewDayLog: "Start New Day Log",
    viewHistory: "View History",

    exitSurvey: "Exit Survey",
    exitLog: "Exit Log",
    confirmExitSurvey:
      "Are you sure you want to exit the survey? Your unsubmitted changes will be lost.",
    confirmExitLog:
      "Are you sure you want to exit the log? Your unsubmitted changes will be lost.",
    confirmDiscardLog:
      "Are you sure you want to discard the current log? Your unsubmitted changes will be lost.",
    surveying: "Surveying",
    newMealLog: "New Meal Log",
    unnamed: "Unnamed",

    today: "Today",
    yesterday: "Yesterday",
    search: "Search",

    // Survey dashboard
    surveyDashboard_Title: "Dashboard",
    surveyDashboard_Respondents: "Respondents",
    surveyDashboard_Days: "Logged Days",
    surveyDashboard_Meals: "Meal Logs",
    surveyDashboard_FishItems: "Fish Items",
    surveyDashboard_LastLog: "Last log date",
    surveyDashboard_PendingSync: "Pending sync",
    surveyDashboard_AdvancedStats: "Advanced Stats",
    surveyDashboard_TimeStats: "Survey Time Stats",
    surveyDashboard_AvgTime: "Avg Time",
    surveyDashboard_MinTime: "Min Time",
    surveyDashboard_MaxTime: "Max Time",
    surveyDashboard_SessionDistribution: "Session Distribution",
    surveyDashboard_LessThan1Min: "< 1 min",
    surveyDashboard_1To5Min: "1-5 min",
    surveyDashboard_5To10Min: "5-10 min",
    surveyDashboard_MoreThan10Min: "> 10 min",
    surveyDashboard_AvgFishPerMeal: "Avg Fish/Meal",
    surveyDashboard_TopMealType: "Top Meal Type",
    surveyDashboard_FishVariations: "Fish Varieties",
    surveyDashboard_MostConsumedFish: "Most Consumed",
    surveyDashboard_RecentLogs: "Recent Logs",
    surveyDashboard_ViewAll: "View All",

    // All Logs page
    allLogs_Title: "All Logs",
    allLogs_SortByConsumption: "By Consumption Date",
    allLogs_SortByLogging: "By Logging Date",
    allLogs_NoLogs: "No logs available",
    allLogs_Consumed: "Consumed",
    allLogs_Logged: "Logged",
    allLogs_TotalLogs: "Total Logs",

    // Auth / Settings / PIN
    auth_welcomeBack: "Welcome Back",
    auth_loginSubtitle: "Sign in to continue",
    auth_enterUsername: "Enter your username or email",
    auth_enterPassword: "Enter your password",
    auth_usernameLabel: "Username/Email",
    auth_passwordLabel: "Password",
    auth_or: "or",
    auth_loggingIn: "Logging in...",
    auth_login: "Login",
    auth_creatingAccount: "Creating Account...",
    auth_alreadyHaveAccount: "Already have an account?",
    auth_loginError: "Login failed. Please try again.",
    auth_tapForDetails: "Tap for details",
    auth_loginErrorTitle: "Login Failed",
    auth_loginErrorSubtitle: "We couldn't log you in. Here are the details:",
    auth_errorMessage: "Error Message",
    auth_attemptedUsername: "Attempted Username",
    auth_statusCode: "Status Code",
    auth_timestamp: "Timestamp",
    auth_technicalDetails: "Technical Details (for support)",
    auth_suggestions: "What you can try:",
    auth_suggestion1: "Check your username and password",
    auth_suggestion2: "Ensure you have an active internet connection",
    auth_suggestion3: "Contact support if the problem persists",
    auth_usePinInstead: "Use PIN instead",
    auth_loginViaPin: "Login via PIN",
    auth_createNewAccount: "Create New Account",
    auth_createAccountTitle: "Create Account",
    auth_signupSubtitle: "Sign up to get started",
    auth_fullName: "Full Name",
    auth_enterFullName: "Enter your full name",
    auth_enterEmail: "Enter your email",
    auth_chooseUsername: "Choose a username",
    auth_usernameHint: "Letters, numbers, hyphens and underscores only",
    auth_usernameTooShort: "Username must be at least 3 characters",
    auth_usernameMustStartWithLetter: "Username must start with a letter",
    auth_usernameInvalidChars: "Only letters, numbers, hyphens and underscores",
    auth_usernameTaken: "This username is already taken",
    auth_invalidEmail: "Please enter a valid email address",
    auth_channel: "Channel",
    auth_loadingChannels: "Loading channels...",
    auth_selectChannel: "Select your channel",
    auth_noChannelsAvailable: "No channels available. Please try again later.",
    auth_confirmPassword: "Confirm Password",
    auth_confirmPasswordPlaceholder: "Confirm your password",
    auth_passwordHint: "Must be at least 8 characters",
    auth_passwordMismatch: "Passwords do not match",
    auth_passwordTooShort: "Password must be at least 8 characters",
    auth_signupError: "Registration failed. Please try again.",
    auth_continueWithoutLogin: "Continue Without Login",
    auth_offlineNote: "Offline mode: Changes will not be synced",
    auth_offlineWarning:
      "Continuing without login means your data will not be synced to the server. You can only view and add data locally. Do you want to continue?",

    pin_enterPin: "Enter PIN",
    pin_enterPinToUnlock: "Enter your PIN to unlock the app",
    pin_incorrectPin: "Incorrect PIN. Try again.",
    pin_pinLocked: "Too many attempts. Please use forgot PIN.",
    pin_attemptsRemaining: "Attempts remaining",
    pin_forgotPin: "Forgot PIN?",
    pin_cancel: "Cancel",
    pin_pinError: "Error validating PIN",

    pin_setupPin: "Setup PIN",
    pin_changePin: "Change PIN",
    pin_confirmPin: "Confirm PIN",
    pin_enterNewPin: "Enter a 4-digit PIN",
    pin_reenterPin: "Re-enter your PIN to confirm",
    pin_pinMismatch: "PINs do not match. Please try again.",
    pin_invalidPin: "Invalid PIN. Please use 4 digits.",
    pin_pinSaveError: "Failed to save PIN. Please try again.",
    pin_note: "Note",
    pin_securityNote:
      "Your PIN will be required each time you open the app. Make sure to remember it.",
    pin_savingPin: "Saving PIN...",

    settings_title: "Settings",
    settings_userGuide: "User Guide",
    settings_helpManual: "Help Manual",
    settings_syncSettings: "Sync Settings",
    settings_manualSync: "Manual Sync",
    settings_syncNow: "Sync Now",
    settings_syncing: "Syncing…",
    settings_syncInProgress: "Sync is running in the background",
    settings_pendingChanges: "Pending changes",
    settings_lastSync: "Last sync",
    settings_never: "Never",
    settings_syncFailed: "Sync failed",
    settings_autoSync: "Auto Sync",
    settings_autoSyncDesc: "Automatically sync data in background",
    settings_syncInterval: "Sync Interval (minutes)",
    settings_wifiOnly: "WiFi Only",
    settings_wifiOnlyDesc: "Only sync when connected to WiFi",
    settings_security: "Security",
    settings_pinLock: "PIN Lock",
    settings_pinLockDesc: "Require PIN to open app",
    settings_pinEnabled: "PIN is enabled",
    settings_pinNotEnabled: "PIN is not enabled",
    settings_confirmDisablePin: "Disable PIN lock on this device?",
    settings_biometric: "Biometric",
    settings_biometricDesc: "Use fingerprint/face ID",
    settings_preferences: "Preferences",
    settings_language: "Language",
    settings_theme: "Theme",
    settings_light: "Light",
    settings_dark: "Dark",
    settings_system: "System",
    settings_advanced: "Advanced",
    settings_developerMode: "Developer Mode",
    settings_developerModeDesc: "Show debug information",
    settings_backupRestore: "Backup & Restore",
    settings_backupRestoreDesc: "Download a backup of your local data or restore it from a JSON file.",
    settings_downloadBackup: "Download Backup",
    settings_restoreFromBackup: "Restore from Backup",
    settings_confirmRestore:
      "This will overwrite your local data from the backup file. Continue?",
    settings_backupCreated: "Backup downloaded.",
    settings_backupFailed: "Backup failed. Please try again.",
    settings_restoreSuccess: "Restore completed. The app will reload.",
    settings_restoreFailed: "Restore failed. Please check the backup file.",
    settings_restoring: "Restoring…",
    settings_clearAllData: "Clear All Data",
    settings_mergeUsers: "Merge Users",
    settings_manageOrphanedRecords: "Manage Orphaned Records",
    settings_viewDebugLogs: "View Debug Logs",
    settings_storageType: "Data Storage Type",
    settings_storageTypeDesc: "Choose how your data is stored locally. SQLite provides better performance for large datasets.",
    settings_switchingStorage: "Switching storage, please wait...",
    
    // Survey Settings
    settings_surveySettings: "Survey Settings",
    settings_surveySettingsDesc: "Configure which fields to show when adding or editing person records in survey mode.",
    settings_surveyNameFieldLabel: "Name Field Label",
    settings_surveyNameFullNameOrId: "Full Name or ID",
    settings_surveyNameIdOnly: "ID Only",
    settings_surveyFieldVisibility: "Field Visibility",
    settings_surveyShowMobile: "Show Contact",
    settings_surveyShowAge: "Show Age",
    settings_surveyMinAge: "Minimum Age",
    settings_surveyMaxAge: "Maximum Age",
    settings_surveyAgeLimitsDesc: "Set the allowed age range for respondents",
    settings_surveyShowSex: "Show Sex",
    settings_surveyShowOccupation: "Show Occupation",
    settings_surveyShowIncome: "Show Monthly Income",
    settings_mealLogging: "Meal Logging",
    settings_restrictMealsByTime: "Restrict meals by time of day",
    settings_showNutrientOutcome: "Show Nutrient Outcome",
    settings_showNutrientOutcomeDesc: "Display nutrient information during meal logging and in log history",
    settings_showNutrientDRI: "Show nutrient outcome based on daily recommended intake (ideal)",
    settings_showNutrientDRIDesc: "When enabled, nutrient amounts are shown relative to Daily Recommended Intake. When disabled, only absolute amounts are displayed.",
    settings_surveyShowAddress: "Show Address",
    settings_surveyAddressType: "Address Type",
    settings_surveyAddressTypeDesc: "Choose between Bangladesh-specific multi-field address or a general single field.",
    settings_surveyAddressBangladesh: "Bangladesh Specific",
    settings_surveyAddressGeneral: "General",
    settings_surveyUIOptions: "Survey UI Options",
    settings_surveyShowBothLanguageNames: "Show Both English and Local Names in Fish List",
    settings_surveyShowBothLanguageNamesDesc: "Display both English and Bangla fish names in survey",
    settings_surveyOnlyShowRecipesWithImages: "Only Show Recipes With Images",
    settings_surveyOnlyShowRecipesWithImagesDesc: "Hide recipe options without reference images",

    settings_surveyExtraInfoOptions: "Extra Information Options",
    settings_surveyShowMealLocation: "Show Meal Location",
    settings_surveyShowMealLocationDesc: "Ask where the meal was consumed",
    settings_surveyShowFishSource: "Show Fish Source",
    settings_surveyShowFishSourceDesc: "Ask where the fish came from",
    settings_surveyShowPreparedBy: "Show Prepared By",
    settings_surveyShowPreparedByDesc: "Ask who prepared the meal",
    settings_surveyShowCoIngredients: "Show Co-Ingredients",
    settings_surveyShowCoIngredientsDesc: "Ask about vegetables cooked with fish",
    settings_surveyShowPeopleSharing: "Show People Sharing",
    settings_surveyShowPeopleSharingDesc: "Ask how many people shared the meal",
    settings_surveyAskExtraInfo: "Ask for additional info",
    settings_surveyAskExtraInfoDesc: "Show additional information questions during logging",
    settings_surveyShowNotes: "Show additional notes",
    settings_surveyShowNotesDesc: "Show a text area to enter cooking ingredients and notes",
    settings_extraInfo_SoftBoneToggle: "Consumed With Soft Bone",
    settings_extraInfo_SoftBoneYes: "Yes",
    settings_extraInfo_SoftBoneNo: "No",
    
    // Manage Orphaned Records
    orphanedRecords_title: "Manage Orphaned Records",
    orphanedRecords_description: "Import person records from deleted or lost surveyor accounts. Useful for data recovery after server crashes or account deletions.",
    orphanedRecords_currentUser: "Current User",
    orphanedRecords_groupsTitle: "Orphaned Record Groups",
    orphanedRecords_orphaned: "Orphaned (No server account)",
    orphanedRecords_active: "Active",
    orphanedRecords_checkingStatus: "Checking status...",
    orphanedRecords_noOrphanedGroups: "No orphaned records found",
    orphanedRecords_importSelected: "Import Selected",
    orphanedRecords_deleteOrphaned: "Delete Orphaned",
    orphanedRecords_confirmImport: "Are you sure you want to import {count} person record(s) into your current account? This will update surveyor IDs and mark records for sync.",
    orphanedRecords_confirmDelete: "Are you sure you want to delete {count} orphaned person record(s)? This action cannot be undone.",
    orphanedRecords_importSuccess: "Successfully imported {count} person record(s)",
    orphanedRecords_deleteSuccess: "Successfully deleted {count} orphaned person record(s)",
    orphanedRecords_importError: "Error importing orphaned records",
    orphanedRecords_deleteError: "Error deleting orphaned records",
    orphanedRecords_recordCount: "{count} records",
    
    // Deleted Records
    deletedRecords_localTitle: "Locally Deleted Records",
    deletedRecords_localDescription: "Records deleted on this device but not yet synced to the server. These will be permanently removed after the next sync.",
    deletedRecords_serverTitle: "Server Deleted Records",
    deletedRecords_serverDescription: "Records that have been deleted and synced to the server. These deletions are permanent.",
    deletedRecords_deletedOn: "Deleted on",
    deletedRecords_pendingSync: "Pending Sync",
    deletedRecords_synced: "Synced",
    
    settings_confirmClearData: "This will delete all local data. Are you sure?",
    settings_logout: "Logout",
    settings_confirmLogout: "Log out on this device? You will need to log in again.",
    settings_version: "Version",

    // Common labels used in auth forms
    username: "Username",
    email: "Email",
    password: "Password",
    or: "or",
    and: "and",

    // Pages:
    // Any Fish Today
    anyFishToday_Heading: "Did you consume any fish today?",
    anyOtherFishToday_Heading: "Did you consume any other fish today?",
    anyFishToday_Yes: "Yes",
    anyFishToday_No: "No",
    anyFishToday_ViewTodaysLog: "View Today's Log",
    anyFishToday_ViewTodaysLogModalTitle: "Today's Fish Consumption Log",

    // Any Fish Yesterday
    anyFishYesterday_Heading: "Did you consume any fish yesterday?",
    anyOtherFishYesterday_Heading: "Did you consume any other fish yesterday?",
    anyFishYesterday_Yes: "Yes",
    anyFishYesterday_No: "No",
    anyFishYesterday_ViewYesterdaysLog: "View Yesterday's Log",

    // When last time
    whenLastTime_Heading: "When did you last consume fish?",
    whenLastTime_NoRecentFish: "I did not consume any fish in the last 7 days",

    //Which Meal
    whichMeal_Heading: "Which meal did you consume fish in?",
    mealType_breakfast: "Breakfast",
    mealType_lunch: "Lunch",
    mealType_snack: "Snack",
    mealType_dinner: "Dinner",

    // Which Fish
    whichFish_Heading: "Which fish did you consume?",
    whichFish_SearchPlaceholder: "Search for fish by name",
    whichFish_NoResults: "No results. Try a different search term.",
    whichFish_Other: "Other fish",
    whichFish_OtherFishNameTitle: "Name the fish",
    whichFish_OtherFishNamePlaceholder: "Type the fish name",
    whichFish_OtherFishCommonList: "Or choose a common fish",
    whichFish_OtherFishConfirm: "Confirm",

    // Which Recipe
    whichRecipe_Heading: "How was the {fish} prepared?",
    whichRecipe_fry: "Fried",
    whichRecipe_curry: "Curry",
    whichRecipe_roasted: "Roasted",
    whichRecipe_smoked: "Smoked",
    whichRecipe_grilled: "Grilled",
    whichRecipe_mashed: "Mashed",
    whichRecipe_other: "Other",
    whichRecipe_unknown: "Not sure",

    // Which Amount
    whichAmount_Heading: "Which amount of the {fish} was consumed?",
    whichAmount_or: "Or enter an approximate amount (in grams)",
    whichAmount_customPlaceholder: "Enter weight (grams)",
    whichAmount_otherDirectInputLabel: "Enter the amount consumed (in grams)",
    whichAmount_selectPortionType: "Select Portion Type",
    whichAmount_selectPieceType: "Select Piece Type",
    whichAmount_selectPortionSize: "Select Portion Size",
    portionType_whole: "Whole",
    portionType_piece: "Piece",
    portionType_portion: "Portion",
    
    pieceType_head: "Head",
    pieceType_middle: "Middle",
    pieceType_tail: "Tail",
    pieceType_dorsal: "Dorsal",
    pieceType_ventral: "Ventral",
    portionSize_small: "Small",
    portionSize_medium: "Medium",
    portionSize_large: "Large",
    continue: "Continue",
    error_noFishItem: "No fish item found.",
    error_fishNotFound: "Fish not found in database.",
    estimatedAmount: "Estimated Amount",
    totalWeight: "Total Weight",
    whichAmount_ChooseValidAmount: "Please choose a valid amount.",
    // whichAmount_SelectPortionType: "Please select a portion type.",
    whichAmount_SelectValidPortionCount: "Please select a valid portion count.",
    whichAmount_AddItem: "Add Item",
    whichAmount_NoAmountAlertTitle: "Amount Not Specified",
    whichAmount_NoAmountAlertMessage:
      "You have not specified any amount for this fish item. Please enter an estimated weight or select portion details to proceed.",

    // Units
    unit_g: "g",
    unit_gm: "gm",
    unit_grams: "grams",
    unit_oz: "oz",
    unit_kcal: "kcal",
    unit_mg: "mg",
    unit_μg: "μg",
    unit_μg_rae: "μg RAE",

    yearSymbol: "yrs",

    // Extra info
    extraInfo_Heading: "Additional Info",
    extraInfo_ConsumedHead: "Did you consume the head?",
    extraInfo_ConsumedTail: "Did you consume the tail?",
    extraInfo_SoftBone: "With soft bone?",
    extraInfo_WithoutSoftBone: "Without soft bone?",
    extraInfo_IngredientsLabel: "Cooking ingredients & notes:",
    extraInfo_IngredientsPlaceholder: "e.g., onion, garlic, turmeric, oil, salt...",
    extraInfo_Next: "Next",
    extraInfo_Skip: "Skip",
    extraInfo_ClearAll: "Clear all selections",
    
    // Extra info - New fields
    extraInfo_MealLocation: "Where was the meal consumed?",
    extraInfo_Location_Home: "Home",
    extraInfo_Location_Restaurant: "Restaurant",
    extraInfo_Location_MarketStall: "Market Stall",
    extraInfo_Location_Outdoor: "Outdoor",
    extraInfo_Location_Other: "Other",
    
    extraInfo_FishSource: "Where did the fish come from?",
    extraInfo_Source_Market: "Market",
    extraInfo_Source_SelfCaught: "Self-caught from open source",
    extraInfo_Source_Farmed: "Self-cultured and harvested",
    extraInfo_Source_Gift: "Gift",
    extraInfo_Source_Other: "Other",
    
    extraInfo_PreparedBy: "Who prepared the meal?",
    extraInfo_PreparedBy_Self: "Self",
    extraInfo_PreparedBy_Family: "Family",
    extraInfo_PreparedBy_Restaurant: "Restaurant",
    extraInfo_PreparedBy_Other: "Other",
    
    extraInfo_PeopleSharing: "How many people in your household shared this meal?",
    extraInfo_PeopleSharingHint: "Include yourself in the count",
 
    extraInfo_CoIngredients: "Co-ingredients with fish",
    extraInfo_CoIngredients_Potato: "Potato",
    extraInfo_CoIngredients_OtherVegetables: "Other vegetables",
    extraInfo_CoIngredients_NoVegetable: "No vegetable",
    
    extraInfo_ShowAdvanced: "Show more options",
    extraInfo_HideAdvanced: "Hide options",

    // FishItem
    fishItem_Name: "Fish Name",
    fishItem_Recipe: "Recipe",
    fishItem_ExtraInfo: "Extra Info",
    fishItem_IngredientsLabel: "Ingredients",
    extraInfo_Display_SharedBy: "Shared by",
    extraInfo_PeopleSharing_Short: "people",
    extraInfo_Display_ConsumedAt: "Consumed at",
    extraInfo_Display_Source: "Source",
    extraInfo_Display_PreparedBy: "Prepared by",
    extraInfo_Display_CoIngredients: "With",
    fishItem_PortionType: "Portion Type",
    fishItem_PieceType: "Piece Type",
    fishItem_NumberOfPortions: "No. of Portions",
    fishItem_NumberOfPortions_whole: "No. of fishes",
    fishItem_NumberOfPortions_piece: "No. of pieces",
    fishItem_NumberOfPortions_portion: "No. of portions",
    fishItem_NumberOfPortions_unknown: "No. of portions",
    fishItem_PortionWeight_whole: "Weight per fish",
    fishItem_PortionWeight_piece: "Weight per piece",
    fishItem_PortionWeight_portion: "Weight per portion",
    fishItem_PortionWeight_unknown: "Weight per portion",
    fishItem_TotalQuantity: "Total Quantity",
    fishItem_ConsumedParts: "Consumed Parts",
    fishItem_Head: "Head",
    fishItem_Tail: "Tail",
    fishItem_SoftBone: "With Soft Bone",
    fishItem_WithoutSoftBone: "Without Soft Bone",
    fishItem_Ingredients: "Cooking Notes",
    fishItem_Edit: "Edit Item",
    fishItem_Delete: "Delete Item",

    // Review Fish Item

    reviewFishItem_Heading: "Confirm Fish Entry",
    reviewFishItem_Missing: "No fish item found to review.",
    ReviewFishItem_noFishItem: "No fish item found to review.",
    ReviewFishItem_noMealRecord: "No meal record found to review.",
    reviewFishItem_Confirm: "Add to Meal",
    reviewFishItem_ConfirmAndFinish: "Add to Meal and Finish",
    reviewFishItem_Edit: "Edit Entry",
    reviewFishItem_EditWhat: "What would you like to edit?",
    reviewFishItem_EditFish: "Fish Species",
    reviewFishItem_EditRecipe: "Recipe",
    reviewFishItem_EditAmount: "Amount/Portion",
    reviewFishItem_EditAdditionalInfo: "Additional Info",
    reviewFishItem_CancelEdit: "Cancel",
    reviewFishItem_Delete: "Delete Entry",
    reviewFishItem_DeleteItem: "Delete Item",
    reviewFishItem_Fish: "Fish",
    reviewFishItem_ConfirmDelete: "Are you sure you want to delete this entry?",
    reviewFishItem_commitEdits: "Commit Edits",
    reviewFishItem_SaveVerificationFailed: "Failed to verify that the data was saved successfully. Please check your recent logs to confirm the meal was recorded.",

    // Add Another Fish Item
    addAnotherFishItem_Heading: "Would you like to add another fish item?",
    addAnotherFishItem_SameFish: "Yes, of the same fish",
    addAnotherFishItem_DifferentFish: "Yes, add another",
    addAnotherFishItem_Yes: "Yes, add another",
    addAnotherFishItem_No: "No, continue",

    // Review log
    reviewLog_Heading: "Review Your Meal Log",
    reviewLog_MissingRecord: "No meal record found to review.",
    reviewLog_GoBack: "Go Back",
    reviewLog_CloseSurvey: "Close Survey",
    reviewLog_Date: "Date",
    reviewLog_MealType: "Meal Type",
    reviewLog_TotalItems: "Number of Fish Items",
    reviewLog_FishItem: "Item",
    reviewLog_pieces: "pieces",
    reviewLog_piece: "piece",
    reviewLog_TotalWeight: "Total Weight",
    reviewLog_NutritionalValue: "Nutritional Value",
    reviewLog_Confirm: "Confirm and Submit",
    reviewLog_Saving: "Saving...",
    reviewLog_AddAnotherFishItem: "Add Another Item",
    reviewLog_NoFishItemsToSave: "No fish items found to save.",
    reviewLog_NoFishItemsToSaveConfirmToProceed:
      "Are you sure you want to proceed without saving any fish items?",
    reviewLog_UpdatedAt: "Last Updated At",
    reviewLog_SaveVerificationFailed: "Failed to verify that the data was saved successfully. Please check your recent logs to confirm the meal was recorded.",

    // Meal Log Session Info
    mealLog_SessionTime: "Log session time",
    mealLog_Minutes: "minutes",
    mealLog_Seconds: "seconds",
    mealLog_TotalIn: "total in",
    mealLog_Sessions: "sessions",
    mealLog_Session: "Session",
    mealLog_FishItems: "Fish items",

    // Submitted
    submitted_Heading: "Meal recorded!",
    submitted_SubText: "Your entry has been saved successfully.",
    submitted_AddAnotherMeal: "Add another meal",
    submitted_Finish: "Finish for today",
    submitted_AddAnotherDay: "Add record for another day",

    // Abort Log
    abortLog_Heading: "End Logging Session?",
    abortLog_SubText: "Are you sure you want to end this logging session? No data will be saved.",
    abortLog_YesEnd: "Yes, End Session",
    abortLog_GoBack: "Go Back",

    // Add Another Day
    addAnotherDay_Heading: "Would you like to log another day?",
    addAnotherDay_Yes: "Yes, add another day",
    addAnotherDay_No: "No, I'm done",

    // Log history
    backToPersonList: "Back to Respondent List",
    loadMore: "Load More",
    logHistory_Heading: "Log History for {name}",
    logHistoryDateLog_NoPerson: "Person not found",
    logHistoryDateLog_NoRecord: "No log record found for this date.",
    logHistoryDateLog_NoFishConsumed: "Did not consume any fish",
    logHistoryDateLog_Heading: "Log for {date}",
    logHistoryDateLog_totalNutrientIntakeHeader: "Total Nutrient Intake",
    logHistoryDateLog_totalNutrientIntakeSubheader:
      "(based on daily recommended intake)",
    logHistoryDateLog_totalConsumedWeightHeader: "Total Consumed Fish Weight",
    logHistoryDateLog_Back: "Back to Calendar View",
    logHistoryDateLog_BackToCalendar: "Back to Calendar View",
    logHistoryDateLog_BackToAllLogs: "Back to All Logs",
    logHistoryDateLog_BackToSurvey: "Back to Survey Dashboard",
    logHistoryDateLog_AddNewMeal: "Add New Meal",
    saving: "Saving...",
    logHistoryDateLog_DeleteFishItemConfirm:
      "Are you sure you want to delete this fish item?",
    logHistoryDateLog_NoMealsLeft:
      "No meals left for this day. The day record has been removed.",
    logHistoryDateLog_FishItemRemoved: "The fish item has been removed.",
    logHistory_goToCurrentMonth: "Go to Current Month",
    logHistory_monthlySummary: "Monthly Summary",
    
    // Monthly Stats
    monthlyStats_heading: "Monthly Summary",
    monthlyStats_daysWithLogs: "Days with Logs",
    monthlyStats_totalMeals: "Total Meals",
    monthlyStats_totalFishWeight: "Total Fish Consumed",
    monthlyStats_FishConsumed: "Fish Consumed",
    monthlyStats_fishBySpecies: "Fish by Species",
    monthlyStats_times: "times",
    monthlyStats_totalNutrients: "Total Nutrient Intake",
    monthlyStats_ofDRI: "of DRI",
    monthlyStats_note: "Note",
    monthlyStats_driNote: "DRI (Daily Reference Intake) percentages are shown for daily average view to compare against recommended adult daily nutrient intake values.",
    monthlyStats_excludedNote: "Nutrient totals exclude entries without nutrient data (e.g., \"Other\" fish).",

    //Nutrients:
    protein: "Protein",
    fat: "Fat",
    energy: "Energy",
    carbohydrates: "Carbohydrates",
    calories: "Calories",
    omega3: "Omega-3",
    omega6: "Omega-6",
    calcium: "Calcium",
    iron: "Iron",
    zinc: "Zinc",
    magnesium: "Magnesium",
    phosphorus: "Phosphorus",
    potassium: "Potassium",
    sodium: "Sodium",
    iodine: "Iodine",
    copper: "Copper",
    manganese: "Manganese",
    selenium: "Selenium",
    sulphur: "Sulphur",
    vitaminA: "Vitamin A",
    vitaminD: "Vitamin D",
    vitaminE: "Vitamin E",
    thiamin: "Thiamin (B1)",
    riboflavin: "Riboflavin (B2)",
    niacin: "Niacin (B3)",
    vitaminB6: "Vitamin B6",
    folate: "Folate (B9)",
    vitaminB12: "Vitamin B12",
    showMoreNutrients: "+ {count} more",
    showLessNutrients: "Show less",
  },
  bn: {
    appTitle: "MyAquaFood",
    welcome: "MyAquaFood এ স্বাগতম",
    description: "আপনার মাছের খাওয়া এবং পুষ্টির গ্রহণ সহজে ট্র্যাক করুন।",
    confirmBackToHome: "আপনি কি নিশ্চিত যে আপনি হোমে ফিরে যেতে চান?",
    confirmBackToHome_HaveUnsavedChanges:
      "আপনি কি সত্যিই হোমে ফিরে যেতে চান? আপনার অসংরক্ষিত পরিবর্তনগুলি হারিয়ে যাবে।",
    surveyMode: "জরিপ মোড",
    userMode: "ইউজার মোড",
    profile: "প্রোফাইল",
    editProfile: "প্রোফাইল সম্পাদনা",
    updateProfile: "আপডেট করুন",
    profileNotFound: "প্রোফাইল পাওয়া যায়নি",
    profileUpdated: "প্রোফাইল সফলভাবে আপডেট হয়েছে।",
    respondentDeleted: "জরিপকৃত ব্যক্তির প্রোফাইল সফলভাবে মুছে ফেলা হয়েছে।",
    deleteAccount: "অ্যাকাউন্ট মুছুন",
    deleteAccountTitle: "অ্যাকাউন্ট মুছুন",
    deleteAccountWarning: "আপনি কি নিশ্চিত আপনার অ্যাকাউন্ট মুছে ফেলতে চান? এই কাজটি পূর্বাবস্থায় ফিরিয়ে আনা যাবে না।",
    deleteAccountImplications: "আপনার অ্যাকাউন্ট মুছে ফেললে স্থায়ীভাবে মুছে যাবে:",
    deleteAccountImplication1: "আপনার সমস্ত মাছ খাওয়ার লগ এবং ইতিহাস",
    deleteAccountImplication2: "আপনার ব্যক্তিগত প্রোফাইল তথ্য",
    deleteAccountImplication3: "সমস্ত পুষ্টিগত গণনা এবং পরিসংখ্যান",
    deleteAccountImplication4: "আপনার অ্যাকাউন্ট প্রবেশাধিকার এবং অ্যাক্সেস",
    deleteAccountConfirmPassword: "মুছে ফেলা নিশ্চিত করতে আপনার পাসওয়ার্ড দিন",
    deleteAccountPasswordPlaceholder: "আপনার পাসওয়ার্ড দিন",
    deleteAccountConfirmButton: "আমার অ্যাকাউন্ট মুছুন",
    deleteAccountCancelButton: "বাতিল",
    deleteAccountSuccess: "আপনার অ্যাকাউন্ট সফলভাবে মুছে ফেলা হয়েছে।",
    deleteAccountError: "অ্যাকাউন্ট মুছতে ব্যর্থ। আবার চেষ্টা করুন।",
    deleteAccountWrongPassword: "ভুল পাসওয়ার্ড। আবার চেষ্টা করুন।",
    profileOptions: "প্রোফাইল বিকল্প",
    loading: "লোড হচ্ছে...",
    invalidID: "অবৈধ আইডি",
    surveyModeDescription:
      "এই মোডে, আপনি অন্যদের পক্ষে (যেমন জরিপ উত্তরদাতা) মাছ খাওয়ার ডেটা রেকর্ড করতে পারেন। প্রতিটি ব্যক্তির নিজস্ব রেকর্ড থাকবে যা দৈনিক খাবারের লগ এবং পুষ্টির হিসাব অন্তর্ভুক্ত করবে।",
    // userModeDescription:
    //   "আপনার দৈনিক মাছ খাওয়া ট্র্যাক করুন। খাবার লগ করুন, মাছের প্রজাতি নির্বাচন করুন, এবং দেখুন আপনার ডায়েট থেকে কতটুকু পুষ্টি পাচ্ছেন।",
    userModeDescription: "আপনার দৈনিক মাছ খাওয়া ট্র্যাক করুন।",

    addNewPerson: "নতুন উত্তরদাতা",
    addNewPersonShort: "নতুন উত্তরদাতা",
    id: "আইডি",
    name: "সম্পূর্ণ নাম অথবা আইডি",
    age: "বয়স",
    sex: "লিঙ্গ",
    selectSex: "লিঙ্গ নির্বাচন করুন",
    male: "পুরুষ",
    female: "মহিলা",
    other: "অন্যান্য",
    prefer_not_to_say: "আমি বলতে চাই না",
    not_specified: "নির্দিষ্ট নয়",
    mobile: "যোগাযোগ",
    occupation: "পেশা",
    selectOccupation: "পেশা নির্বাচন করুন",
    monthlyIncome: "মাসিক আয়",
    farmer: "কৃষক",
    fisherman: "মৎস্যজীবী",
    government_officer: "সরকারি কর্মকর্তা",
    government_employee: "সরকারি কর্মচারী",
    private_service: "বেসরকারি চাকুরিজীবী",
    business: "ব্যবসায়ী",
    housewife: "গৃহিণী",
    student: "ছাত্র / ছাত্রী",
    teacher: "শিক্ষক / শিক্ষিকা",
    address: "ঠিকানা",
    district: "জেলা",
    upazila: "উপজেলা",
    union: "ইউনিয়ন",
    village: "গ্রাম",
    notes: "নোট (ঐচ্ছিক)",
    save: "✅ সংরক্ষণ করুন",
    saveAndStart: "🚀 জরিপ শুরু করুন",
    SaveAndSkipSurvey: "সংরক্ষণ করুন এবং জরিপ এড়িয়ে যান",
    delete: "বাদ দিন",
    back: "ফিরে যান",
    requiredFields: "অনুগ্রহ করে সব প্রয়োজনীয় তথ্য পূরণ করুন।",
    invalidAge: "অনুগ্রহ করে {min} থেকে {max} বছরের মধ্যে একটি বৈধ বয়স প্রবেশ করুন।",
    personWithMobileExists: "এই যোগাযোগের একজন ব্যক্তি ইতিমধ্যে বিদ্যমান।",
    setAvatar: "ছবি সেট করুন",
    changeAvatar: "ছবি পরিবর্তন করুন",

    surveyedPersonsList: "জরিপকৃতদের তালিকা",
    yourSurveyedPersons: "আপনার জরিপকৃত ব্যক্তিরা",
    viewAllPersons: "সকল জরিপকৃত ব্যক্তিদের তালিকা দেখুন",
    noRespondents: "এখনো কোনো জরিপকৃত ব্যক্তি যোগ করা হয়নি।",
    confirmDeleteRespondent:
      "আপনি কি নিশ্চিত যে এই জরিপকৃত ব্যক্তিকে মুছে ফেলতে চান? এই পদক্ষেপটি পূর্বাবস্থায় ফিরিয়ে আনা যাবে না।",

    logDay: "নতুন লগ",
    allLogs: "সকল লগ",

    didYouEatToday: "আপনি কি আজ মাছ খেয়েছেন?",
    didYouEatYesterday: "আপনি কি গতকাল মাছ খেয়েছেন?",
    whenLastAte: "আপনি শেষ কবে মাছ খেয়েছেন?",
    yes: "হ্যাঁ",
    no: "না",
    closeSurvey: "জরিপ বন্ধ করুন",
    close: "বন্ধ করুন",
    cancel: "বাতিল",

    startNewDayLog: "নতুন দিনের লগ শুরু করুন",
    viewHistory: "ইতিহাস দেখুন",

    exitSurvey: "জরিপ থেকে বের হন",
    exitLog: "লগ থেকে বের হন",
    confirmExitSurvey:
      "আপনি কি নিশ্চিত যে আপনি জরিপ থেকে বের হতে চান? আপনার অপ্রেরিত পরিবর্তনগুলি হারিয়ে যাবে।",
    confirmExitLog:
      "আপনি কি নিশ্চিত যে আপনি লগ থেকে বের হতে চান? আপনার অপ্রেরিত পরিবর্তনগুলি হারিয়ে যাবে।",
    confirmDiscardLog:
      "বর্তমান লগটি বাতিল করতে চান? আপনার অপ্রেরিত পরিবর্তনগুলি হারিয়ে যাবে।",
    surveying: "জরিপ চলছে",
    newMealLog: "নতুন খাবারের লগ",
    unnamed: "নামহীন",

    today: "আজ",
    yesterday: "গতকাল",
    search: "খুঁজুন",

    // Survey dashboard
    surveyDashboard_Title: "ড্যাশবোর্ড",
    surveyDashboard_Respondents: "উত্তরদাতা",
    surveyDashboard_Days: "লগ করা দিন",
    surveyDashboard_Meals: "মিল লগ",
    surveyDashboard_FishItems: "মাছের আইটেম",
    surveyDashboard_LastLog: "সর্বশেষ লগের তারিখ",
    surveyDashboard_PendingSync: "সিঙ্ক বাকি",
    surveyDashboard_AdvancedStats: "উন্নত পরিসংখ্যান",
    surveyDashboard_TimeStats: "সমীক্ষার সময় পরিসংখ্যান",
    surveyDashboard_AvgTime: "গড় সময়",
    surveyDashboard_MinTime: "সর্বনিম্ন সময়",
    surveyDashboard_MaxTime: "সর্বোচ্চ সময়",
    surveyDashboard_SessionDistribution: "সেশন বিতরণ",
    surveyDashboard_LessThan1Min: "< ১ মিনিট",
    surveyDashboard_1To5Min: "১-৫ মিনিট",
    surveyDashboard_5To10Min: "৫-১০ মিনিট",
    surveyDashboard_MoreThan10Min: "> ১০ মিনিট",
    surveyDashboard_AvgFishPerMeal: "গড় মাছ/খাবার",
    surveyDashboard_TopMealType: "শীর্ষ খাবারের ধরন",
    surveyDashboard_FishVariations: "মাছের প্রকারভেদ",
    surveyDashboard_MostConsumedFish: "সবচেয়ে খাওয়া মাছ",
    surveyDashboard_RecentLogs: "সাম্প্রতিক লগ",
    surveyDashboard_ViewAll: "সব দেখুন",

    // All Logs page
    allLogs_Title: "সকল লগ",
    allLogs_SortByConsumption: "খাওয়ার তারিখ অনুসারে",
    allLogs_SortByLogging: "লগিং তারিখ অনুসারে",
    allLogs_NoLogs: "কোনো লগ পাওয়া যায়নি",
    allLogs_Consumed: "খাওয়া হয়েছে",
    allLogs_Logged: "লগ করা হয়েছে",
    allLogs_TotalLogs: "মোট লগ",

    // Auth / Settings / PIN
    auth_welcomeBack: "স্বাগতম",
    auth_loginSubtitle: "সাইন ইন করুন",
    auth_enterUsername: "আপনার ইউজারনেম বা ইমেইল লিখুন",
    auth_enterPassword: "আপনার পাসওয়ার্ড লিখুন",
    auth_usernameLabel: "ইউজারনেম/ইমেইল",
    auth_passwordLabel: "পাসওয়ার্ড",
    auth_or: "অথবা",
    auth_loggingIn: "লগইন হচ্ছে...",
    auth_login: "লগইন",
    auth_creatingAccount: "অ্যাকাউন্ট তৈরি হচ্ছে...",
    auth_alreadyHaveAccount: "আগে থেকেই অ্যাকাউন্ট আছে?",
    auth_loginError: "লগইন ব্যর্থ হয়েছে। আবার চেষ্টা করুন।",
    auth_tapForDetails: "বিস্তারিত দেখতে ট্যাপ করুন",
    auth_loginErrorTitle: "লগইন ব্যর্থ হয়েছে",
    auth_loginErrorSubtitle: "আমরা আপনাকে লগইন করাতে পারিনি। এখানে বিস্তারিত দেওয়া হল:",
    auth_errorMessage: "ত্রুটি বার্তা",
    auth_attemptedUsername: "চেষ্টা করা ইউজারনেম",
    auth_statusCode: "স্ট্যাটাস কোড",
    auth_timestamp: "সময়",
    auth_technicalDetails: "প্রযুক্তিগত বিস্তারিত (সাপোর্টের জন্য)",
    auth_suggestions: "আপনি যা চেষ্টা করতে পারেন:",
    auth_suggestion1: "আপনার ইউজারনেম এবং পাসওয়ার্ড পরীক্ষা করুন",
    auth_suggestion2: "আপনার ইন্টারনেট সংযোগ সক্রিয় আছে কিনা নিশ্চিত করুন",
    auth_suggestion3: "সমস্যা অব্যাহত থাকলে সাপোর্টে যোগাযোগ করুন",
    auth_usePinInstead: "পাসওয়ার্ডের বদলে PIN ব্যবহার করুন",
    auth_loginViaPin: "পিন দিয়ে লগইন",
    auth_createNewAccount: "নতুন অ্যাকাউন্ট তৈরি করুন",
    auth_createAccountTitle: "অ্যাকাউন্ট তৈরি করুন",
    auth_signupSubtitle: "শুরু করতে সাইন আপ করুন",
    auth_fullName: "পূর্ণ নাম",
    auth_enterFullName: "আপনার পূর্ণ নাম লিখুন",
    auth_enterEmail: "আপনার ইমেইল লিখুন",
    auth_chooseUsername: "একটি ইউজারনেম নির্বাচন করুন",
    auth_usernameHint: "শুধু অক্ষর, সংখ্যা, হাইফেন এবং আন্ডারস্কোর",
    auth_usernameTooShort: "ইউজারনেম কমপক্ষে ৩ অক্ষরের হতে হবে",
    auth_usernameMustStartWithLetter: "ইউজারনেম অবশ্যই অক্ষর দিয়ে শুরু হতে হবে",
    auth_usernameInvalidChars: "শুধু অক্ষর, সংখ্যা, হাইফেন এবং আন্ডারস্কোর",
    auth_usernameTaken: "এই ইউজারনেমটি ইতিমধ্যে ব্যবহৃত হয়েছে",
    auth_invalidEmail: "দয়া করে একটি বৈধ ইমেইল ঠিকানা লিখুন",
    auth_channel: "চ্যানেল",
    auth_loadingChannels: "চ্যানেল লোড হচ্ছে...",
    auth_selectChannel: "চ্যানেল নির্বাচন করুন",
    auth_noChannelsAvailable: "কোনো চ্যানেল পাওয়া যায়নি। পরে আবার চেষ্টা করুন।",
    auth_confirmPassword: "পাসওয়ার্ড নিশ্চিত করুন",
    auth_confirmPasswordPlaceholder: "আপনার পাসওয়ার্ড আবার লিখুন",
    auth_passwordHint: "কমপক্ষে ৮ অক্ষরের হতে হবে",
    auth_passwordMismatch: "পাসওয়ার্ড মিলছে না",
    auth_passwordTooShort: "পাসওয়ার্ড কমপক্ষে ৮ অক্ষরের হতে হবে",
    auth_signupError: "রেজিস্ট্রেশন ব্যর্থ হয়েছে। আবার চেষ্টা করুন।",
    auth_continueWithoutLogin: "লগইন ছাড়া চালিয়ে যান",
    auth_offlineNote: "অফলাইন মোড: পরিবর্তনগুলো সার্ভারে সিঙ্ক হবে না",
    auth_offlineWarning:
      "লগইন ছাড়া চালিয়ে গেলে আপনার ডেটা সার্ভারে সিঙ্ক হবে না। আপনি শুধু লোকালি ডেটা দেখতে ও যোগ করতে পারবেন। আপনি কি চালিয়ে যেতে চান?",

    pin_enterPin: "PIN লিখুন",
    pin_enterPinToUnlock: "অ্যাপ আনলক করতে আপনার PIN লিখুন",
    pin_incorrectPin: "ভুল PIN। আবার চেষ্টা করুন।",
    pin_pinLocked: "অনেকবার চেষ্টা করা হয়েছে। দয়া করে 'PIN ভুলে গেছেন' ব্যবহার করুন।",
    pin_attemptsRemaining: "অবশিষ্ট চেষ্টা",
    pin_forgotPin: "PIN ভুলে গেছেন?",
    pin_cancel: "বাতিল",
    pin_pinError: "PIN যাচাই করতে সমস্যা হয়েছে",

    pin_setupPin: "PIN সেটআপ করুন",
    pin_changePin: "PIN পরিবর্তন করুন",
    pin_confirmPin: "PIN নিশ্চিত করুন",
    pin_enterNewPin: "৪-সংখ্যার PIN লিখুন",
    pin_reenterPin: "নিশ্চিত করতে PIN আবার লিখুন",
    pin_pinMismatch: "PIN মিলছে না। আবার চেষ্টা করুন।",
    pin_invalidPin: "অবৈধ PIN। দয়া করে ৪টি সংখ্যা ব্যবহার করুন।",
    pin_pinSaveError: "PIN সংরক্ষণ করা যায়নি। আবার চেষ্টা করুন।",
    pin_note: "নোট",
    pin_securityNote:
      "প্রতিবার অ্যাপ খুললে আপনার PIN লাগবে। দয়া করে PIN মনে রাখুন।",
    pin_savingPin: "PIN সংরক্ষণ হচ্ছে...",

    settings_title: "সেটিংস",
    settings_userGuide: "ব্যবহারকারী নির্দেশিকা",
    settings_helpManual: "সহায়তা ম্যানুয়াল",
    settings_syncSettings: "সিঙ্ক সেটিংস",
    settings_manualSync: "ম্যানুয়াল সিঙ্ক",
    settings_syncNow: "এখন সিঙ্ক করুন",
    settings_syncing: "সিঙ্ক হচ্ছে…",
    settings_syncInProgress: "সিঙ্ক ব্যাকগ্রাউন্ডে চলছে",
    settings_pendingChanges: "অপেক্ষমাণ পরিবর্তন",
    settings_lastSync: "শেষ সিঙ্ক",
    settings_never: "কখনও নয়",
    settings_syncFailed: "সিঙ্ক ব্যর্থ হয়েছে",
    settings_autoSync: "অটো সিঙ্ক",
    settings_autoSyncDesc: "ব্যাকগ্রাউন্ডে স্বয়ংক্রিয়ভাবে সিঙ্ক করুন",
    settings_syncInterval: "সিঙ্ক ইন্টারভাল (মিনিট)",
    settings_wifiOnly: "শুধু WiFi",
    settings_wifiOnlyDesc: "শুধু WiFi সংযোগে সিঙ্ক করুন",
    settings_security: "নিরাপত্তা",
    settings_pinLock: "PIN লক",
    settings_pinLockDesc: "অ্যাপ খুলতে PIN প্রয়োজন হবে",
    settings_pinEnabled: "PIN চালু আছে",
    settings_pinNotEnabled: "PIN চালু নেই",
    settings_confirmDisablePin: "এই ডিভাইসে PIN লক বন্ধ করবেন?",
    settings_biometric: "বায়োমেট্রিক",
    settings_biometricDesc: "ফিঙ্গারপ্রিন্ট/ফেস আইডি ব্যবহার করুন",
    settings_preferences: "পছন্দসমূহ",
    settings_language: "ভাষা",
    settings_theme: "থিম",
    settings_light: "লাইট",
    settings_dark: "ডার্ক",
    settings_system: "সিস্টেম",
    settings_advanced: "অ্যাডভান্সড",
    settings_developerMode: "ডেভেলপার মোড",
    settings_developerModeDesc: "ডিবাগ তথ্য দেখান",
    settings_backupRestore: "ব্যাকআপ ও রিস্টোর",
    settings_backupRestoreDesc:
      "আপনার লোকাল ডেটার ব্যাকআপ ডাউনলোড করুন অথবা JSON ফাইল থেকে রিস্টোর করুন।",
    settings_downloadBackup: "ব্যাকআপ ডাউনলোড করুন",
    settings_restoreFromBackup: "ব্যাকআপ থেকে রিস্টোর করুন",
    settings_confirmRestore:
      "এটি ব্যাকআপ ফাইল থেকে আপনার লোকাল ডেটা ওভাররাইট করবে। চালিয়ে যাবেন?",
    settings_backupCreated: "ব্যাকআপ ডাউনলোড হয়েছে।",
    settings_backupFailed: "ব্যাকআপ ব্যর্থ হয়েছে। আবার চেষ্টা করুন।",
    settings_restoreSuccess: "রিস্টোর সম্পন্ন হয়েছে। অ্যাপ রিলোড হবে।",
    settings_restoreFailed: "রিস্টোর ব্যর্থ হয়েছে। ব্যাকআপ ফাইলটি যাচাই করুন।",
    settings_restoring: "রিস্টোর হচ্ছে…",
    settings_clearAllData: "সব ডেটা মুছুন",
    settings_mergeUsers: "ব্যবহারকারী মার্জ করুন",
    settings_manageOrphanedRecords: "অনাথ রেকর্ড ম্যানেজ করুন",
    settings_viewDebugLogs: "ডিবাগ লগ দেখুন",
    settings_storageType: "ডেটা স্টোরেজের ধরন",
    settings_storageTypeDesc: "আপনার ডেটা কীভাবে লোকালি সংরক্ষণ করা হবে তা ঠিক করুন। SQLite বড় ডেটাসেটের জন্য ভালো পারফরম্যান্স প্রদান করে।",
    settings_switchingStorage: "স্টোরেজ পরিবর্তন হচ্ছে, অনুগ্রহ করে অপেক্ষা করুন...",
    
    // Survey Settings
    settings_surveySettings: "সার্ভে সেটিংস",
    settings_surveySettingsDesc: "সার্ভে মোডে ব্যক্তি রেকর্ড যোগ বা সম্পাদনা করার সময় কোন ক্ষেত্রগুলি দেখাতে হবে তা কনফিগার করুন।",
    settings_surveyNameFieldLabel: "নাম ফিল্ড লেবেল",
    settings_surveyNameFullNameOrId: "পূর্ণ নাম বা আইডি",
    settings_surveyNameIdOnly: "শুধু আইডি",
    settings_surveyFieldVisibility: "ক্ষেত্র দৃশ্যমানতা",
    settings_surveyShowMobile: "যোগাযোগ দেখান",
    settings_surveyShowAge: "বয়স দেখান",
    settings_surveyMinAge: "ন্যূনতম বয়স",
    settings_surveyMaxAge: "সর্বোচ্চ বয়স",
    settings_surveyAgeLimitsDesc: "উত্তরদাতাদের জন্য অনুমোদিত বয়সের সীমা সেট করুন",
    settings_surveyShowSex: "লিঙ্গ দেখান",
    settings_surveyShowOccupation: "পেশা দেখান",
    settings_surveyShowIncome: "মাসিক আয় দেখান",
    settings_mealLogging: "খাবার লগিং",
    settings_restrictMealsByTime: "দিনের সময় অনুযায়ী খাবার সীমাবদ্ধ করুন",
    settings_showNutrientOutcome: "পুষ্টির ফলাফল দেখান",
    settings_showNutrientOutcomeDesc: "খাবার লগিং এবং লগ ইতিহাসে পুষ্টির তথ্য প্রদর্শন করুন",
    settings_showNutrientDRI: "দৈনিক প্রস্তাবিত গ্রহণের (আদর্শ) ভিত্তিতে পুষ্টির ফলাফল দেখান",
    settings_showNutrientDRIDesc: "চালু থাকলে, পুষ্টির পরিমাণ দৈনিক প্রস্তাবিত গ্রহণের (DRI) তুলনায় দেখানো হয়। বন্ধ থাকলে শুধুমাত্র পরিমাণ দেখানো হয়।",
    settings_surveyShowAddress: "ঠিকানা দেখান",
    settings_surveyAddressType: "ঠিকানার ধরন",
    settings_surveyAddressTypeDesc: "বাংলাদেশ-নির্দিষ্ট মাল্টি-ফিল্ড ঠিকানা বা একটি সাধারণ একক ক্ষেত্রের মধ্যে বেছে নিন।",
    settings_surveyAddressBangladesh: "বাংলাদেশ নির্দিষ্ট",
    settings_surveyAddressGeneral: "সাধারণ",
    settings_surveyUIOptions: "সার্ভে UI অপশন",
    settings_surveyShowBothLanguageNames: "মাছের তালিকায় ইংরেজি এবং স্থানীয় উভয় নাম দেখান",
    settings_surveyShowBothLanguageNamesDesc: "সার্ভেতে ইংরেজি এবং বাংলা উভয় মাছের নাম প্রদর্শন করুন",
    settings_surveyOnlyShowRecipesWithImages: "শুধুমাত্র ছবি সহ রেসিপি দেখান",
    settings_surveyOnlyShowRecipesWithImagesDesc: "রেফারেন্স ছবি ছাড়া রেসিপি অপশন লুকান",

    settings_surveyExtraInfoOptions: "অতিরিক্ত তথ্য অপশন",
    settings_surveyShowMealLocation: "খাবারের স্থান দেখান",
    settings_surveyShowMealLocationDesc: "খাবার কোথায় খাওয়া হয়েছিল জিজ্ঞাসা করুন",
    settings_surveyShowFishSource: "মাছের উৎস দেখান",
    settings_surveyShowFishSourceDesc: "মাছ কোথা থেকে এসেছে জিজ্ঞাসা করুন",
    settings_surveyShowPreparedBy: "প্রস্তুতকারী দেখান",
    settings_surveyShowPreparedByDesc: "কে খাবার তৈরি করেছে জিজ্ঞাসা করুন",
    settings_surveyShowCoIngredients: "সহ-উপাদান দেখান",
    settings_surveyShowCoIngredientsDesc: "মাছের সাথে রান্না সবজি সম্পর্কে জিজ্ঞাসা করুন",
    settings_surveyShowPeopleSharing: "ভাগাভাগি দেখান",
    settings_surveyShowPeopleSharingDesc: "কতজন খাবার ভাগ করে নিয়েছে জিজ্ঞাসা করুন",
    settings_surveyAskExtraInfo: "অতিরিক্ত তথ্য চাইবেন?",
    settings_surveyAskExtraInfoDesc: "লগিংয়ের সময় অতিরিক্ত তথ্যের প্রশ্ন দেখান",
    settings_surveyShowNotes: "অতিরিক্ত নোট দেখান",
    settings_surveyShowNotesDesc: "রান্নার উপকরণ এবং নোট লেখার জন্য একটি টেক্সট এরিয়া দেখান",
    settings_extraInfo_SoftBoneToggle: "নরম হাড সহ খাওয়া হয়েছে",
    settings_extraInfo_SoftBoneYes: "হ্যাঁ",
    settings_extraInfo_SoftBoneNo: "না",
    
    // Manage Orphaned Records
    orphanedRecords_title: "অনাথ রেকর্ড ম্যানেজ করুন",
    orphanedRecords_description: "মুছে ফেলা বা হারিয়ে যাওয়া সার্ভেয়ার অ্যাকাউন্ট থেকে ব্যক্তির রেকর্ড আমদানি করুন। সার্ভার ক্র্যাশ বা অ্যাকাউন্ট মুছে ফেলার পরে ডেটা পুনরুদ্ধারের জন্য উপযোগী।",
    orphanedRecords_currentUser: "বর্তমান ব্যবহারকারী",
    orphanedRecords_groupsTitle: "অনাথ রেকর্ড গ্রুপ",
    orphanedRecords_orphaned: "অনাথ (সার্ভার অ্যাকাউন্ট নেই)",
    orphanedRecords_active: "সক্রিয়",
    orphanedRecords_checkingStatus: "স্ট্যাটাস পরীক্ষা করা হচ্ছে...",
    orphanedRecords_noOrphanedGroups: "কোনো অনাথ রেকর্ড পাওয়া যায়নি",
    orphanedRecords_importSelected: "নির্বাচিত আমদানি করুন",
    orphanedRecords_deleteOrphaned: "অনাথ মুছুন",
    orphanedRecords_confirmImport: "আপনি কি নিশ্চিত যে আপনি {count} ব্যক্তির রেকর্ড আপনার বর্তমান অ্যাকাউন্টে আমদানি করতে চান? এটি সার্ভেয়ার আইডি আপডেট করবে এবং রেকর্ডগুলিকে সিঙ্কের জন্য চিহ্নিত করবে।",
    orphanedRecords_confirmDelete: "আপনি কি নিশ্চিত যে আপনি {count} অনাথ ব্যক্তির রেকর্ড মুছতে চান? এই পদক্ষেপ পূর্বাবস্থায় ফেরানো যাবে না।",
    orphanedRecords_importSuccess: "সফলভাবে {count} ব্যক্তির রেকর্ড আমদানি করা হয়েছে",
    orphanedRecords_deleteSuccess: "সফলভাবে {count} অনাথ ব্যক্তির রেকর্ড মুছে ফেলা হয়েছে",
    orphanedRecords_importError: "অনাথ রেকর্ড আমদানি করতে ত্রুটি",
    orphanedRecords_deleteError: "অনাথ রেকর্ড মুছতে ত্রুটি",
    orphanedRecords_recordCount: "{count} রেকর্ড",
    
    // Deleted Records
    deletedRecords_localTitle: "স্থানীয়ভাবে মুছে ফেলা রেকর্ড",
    deletedRecords_localDescription: "এই ডিভাইসে মুছে ফেলা রেকর্ড কিন্তু এখনও সার্ভারে সিঙ্ক হয়নি। পরবর্তী সিঙ্কের পরে এগুলি স্থায়ীভাবে সরানো হবে।",
    deletedRecords_serverTitle: "সার্ভারে মুছে ফেলা রেকর্ড",
    deletedRecords_serverDescription: "যে রেকর্ডগুলি মুছে ফেলা হয়েছে এবং সার্ভারে সিঙ্ক করা হয়েছে। এই মুছে ফেলা স্থায়ী।",
    deletedRecords_deletedOn: "মুছে ফেলা হয়েছে",
    deletedRecords_pendingSync: "সিঙ্ক বাকি",
    deletedRecords_synced: "সিঙ্ক হয়েছে",
    
    settings_confirmClearData: "এটি সব লোকাল ডেটা মুছে ফেলবে। আপনি কি নিশ্চিত?",
    settings_logout: "লগআউট",
    settings_confirmLogout:
      "এই ডিভাইসে লগ আউট করবেন? আবার ব্যবহার করতে লগইন করতে হবে।",
    settings_version: "ভার্সন",

    // Common labels used in auth forms
    username: "ইউজারনেম",
    email: "ইমেইল",
    password: "পাসওয়ার্ড",
    or: "অথবা",
    and: "এবং",

    // Pages:
    // Any Fish Today
    anyFishToday_Heading: "আপনি কি আজ কোনো মাছ খেয়েছেন?",
    anyOtherFishToday_Heading: "আপনি কি আজ অন্য কোনো মাছ খেয়েছেন?",
    anyFishToday_Yes: "হ্যাঁ",
    anyFishToday_No: "না",
    anyFishToday_ViewTodaysLog: "আজকের লগ দেখুন",
    anyFishToday_ViewTodaysLogModalTitle: "আজকের মাছ খাওয়ার লগ",

    // Any Fish Yesterday
    anyFishYesterday_Heading: "আপনি কি গতকাল কোনো মাছ খেয়েছেন?",
    anyOtherFishYesterday_Heading: "আপনি কি গতকাল অন্য কোনো মাছ খেয়েছেন?",
    anyFishYesterday_Yes: "হ্যাঁ",
    anyFishYesterday_No: "না",
    anyFishYesterday_ViewYesterdaysLog: "গতকালের লগ দেখুন",

    // When last time
    whenLastTime_Heading: "আপনি শেষ কবে মাছ খেয়েছেন?",
    whenLastTime_NoRecentFish: "গত ৭ দিনে আমি কোনো মাছ খাইনি",

    //Which Meal
    whichMeal_Heading: "আপনি কোন খাবারে মাছ খেয়েছেন?",
    mealType_breakfast: "সকালের খাবার",
    mealType_lunch: "দুপুরের খাবার",
    mealType_snack: "সন্ধ্যার নাস্তা",
    mealType_dinner: "রাতের খাবার",

    // Which Fish
    whichFish_Heading: "আপনি কোন মাছ খেয়েছেন?",
    whichFish_SearchPlaceholder: "মাছের নাম অনুসন্ধান করুন",
    whichFish_NoResults: "কোন ফলাফল পাওয়া যায়নি।",
    whichFish_Other: "অন্যান্য মাছ",
    whichFish_OtherFishNameTitle: "মাছের নাম দিন",
    whichFish_OtherFishNamePlaceholder: "মাছের নাম লিখুন",
    whichFish_OtherFishCommonList: "অথবা একটি প্রচলিত মাছ বেছে নিন",
    whichFish_OtherFishConfirm: "নিশ্চিত করুন",

    // Which Recipe
    whichRecipe_Heading: "আপনি {fish} মাছ কিভাবে খেয়েছেন?",
    whichRecipe_fry: "ভাজা",
    whichRecipe_curry: "ঝোল/ভুনা",
    whichRecipe_roasted: "রোস্ট করা",
    whichRecipe_smoked: "ধূমায়িত",
    whichRecipe_grilled: "গ্রিল",
    whichRecipe_mashed: "ভর্তা",
    whichRecipe_other: "অন্যান্য",
    whichRecipe_unknown: "জানা নেই",

    // Which Amount
    whichAmount_Heading: "আপনি {fish} মাছ কতটুকু খেয়েছেন?",
    whichAmount_selectPortionType: "পরিমাণের ধরন নির্বাচন করুন",
    whichAmount_selectPieceType: "টুকরোর ধরন নির্বাচন করুন",
    whichAmount_selectPortionSize: "পরিমাণের আকার নির্বাচন করুন",
    whichAmount_or: "অথবা আনুমানিক পরিমাণ (গ্রামে) লিখুন",
    whichAmount_customPlaceholder: "গ্রামে পরিমাণ লিখুন",
    whichAmount_otherDirectInputLabel: "খাওয়ার পরিমাণ লিখুন (গ্রামে)",


    portionType_whole: "সম্পূর্ণ মাছ",
    portionType_piece: "টুকরা",
    portionType_portion: "অংশ",
    
    pieceType_head: "মাথা",
    pieceType_middle: "মাঝের অংশ",
    pieceType_tail: "লেজ",
    pieceType_dorsal: "পিঠের অংশ",
    pieceType_ventral: "পেটের অংশ",
    
    portionSize_small: "ছোট",
    portionSize_medium: "মাঝারি",
    portionSize_large: "বড়",
    continue: "চালিয়ে যান",
    error_noFishItem: "কোন মাছের আইটেম পাওয়া যায়নি।",
    error_fishNotFound: "মাছের ডাটাবেসে মাছ পাওয়া যায়নি.",
    estimatedAmount: "আনুমানিক পরিমাণ",
    totalWeight: "মোট ওজন",
    whichAmount_ChooseValidAmount: "অনুগ্রহ করে একটি বৈধ পরিমাণ নির্বাচন করুন।",
    // whichAmount_SelectPortionType:
    //   "অনুগ্রহ করে একটি পরিমাণের ধরন নির্বাচন করুন।",
    whichAmount_SelectValidPortionCount:
      "অনুগ্রহ করে একটি বৈধ অংশের সংখ্যা নির্বাচন করুন।",
    whichAmount_AddItem: "যোগ করুন",
    whichAmount_NoAmountAlertTitle: "কোন পরিমাণ নির্দিষ্ট করা হয়নি",
    whichAmount_NoAmountAlertMessage:
      "আপনি এই মাছের আইটেমের জন্য কোন পরিমাণ নির্দিষ্ট করেননি। অনুগ্রহ করে একটি আনুমানিক ওজন লিখুন অথবা পরিমাণের বিবরণ নির্বাচন করুন।",


    // Units
    unit_g: "গ্রাম",
    unit_gm: "গ্রাম",
    unit_grams: "গ্রাম",
    unit_oz: "আউন্স",
    unit_kcal: "ক্যালোরি",
    unit_mg: "মি.গ্রা.",
    unit_μg: "মাইক্রোগ্রাম",
    unit_μg_rae: "মাইক্রোগ্রাম RAE",

    yearSymbol: "বছর",

    // Extra info
    extraInfo_Heading: "অতিরিক্ত তথ্য",
    extraInfo_ConsumedHead: "মাছের মাথা খেয়েছেন?",
    extraInfo_ConsumedTail: "মাছের লেজ খেয়েছেন?",
    extraInfo_SoftBone: "নরম হাড় সহ খেয়েছেন?",
    extraInfo_WithoutSoftBone: "নরম হাড় ছাড়া খেয়েছেন?",
    extraInfo_IngredientsLabel: "রান্নার উপকরণ নোট করুন",
    extraInfo_Next: "পরবর্তী",
    extraInfo_IngredientsPlaceholder: "যেমন: পেঁয়াজ, রসুন, হলুদ, তেল, লবণ...",
    extraInfo_Skip: "এড়িয়ে যান",
    extraInfo_ClearAll: "সব মুছুন",
    extraInfo_MealLocation: "খাবার কোথায় খেয়েছেন?",
    extraInfo_Location_Home: "বাড়িতে",
    extraInfo_Location_Restaurant: "রেস্তোরাঁ",
    extraInfo_Location_MarketStall: "বাজারের দোকান",
    extraInfo_Location_Outdoor: "বাইরে",
    extraInfo_Location_Other: "অন্যত্র",
    extraInfo_FishSource: "মাছ কোথা থেকে এসেছে?",
    extraInfo_Source_Market: "বাজার",
    extraInfo_Source_SelfCaught: "নিজে ধরা (উন্মুক্ত জ্বলাশয় থেকে)",
    extraInfo_Source_Farmed: "নিজে চাষ এবং সংগ্রহকৃত",
    extraInfo_Source_Gift: "উপহার",
    extraInfo_Source_Other: "অন্যান্য",
    extraInfo_PreparedBy: "খাবার কে রান্না করেছে?",
    extraInfo_PreparedBy_Self: "নিজে",
    extraInfo_PreparedBy_Family: "পরিবার",
    extraInfo_PreparedBy_Restaurant: "রেস্তোরাঁ",
    extraInfo_PreparedBy_Other: "অন্যান্য",
    extraInfo_PeopleSharing: "পরিবারের কতজন এই একই খাবার খেয়েছে?",
    extraInfo_PeopleSharingHint: "নিজেকেও গণনায় যোগ করুন",
    
    extraInfo_CoIngredients: "মাছের সাথে সহ-উপাদান",
    extraInfo_CoIngredients_Potato: "আলু",
    extraInfo_CoIngredients_OtherVegetables: "অন্যান্য সবজি",
    extraInfo_CoIngredients_NoVegetable: "কোন সবজি ছাড়া",
    
    extraInfo_ShowAdvanced: "আরও অতিরিক্ত তথ্য দেখান",
    extraInfo_HideAdvanced: "অতিরিক্ত তথ্য লুকান",

    // FishItem
    fishItem_Name: "মাছের নাম",
    fishItem_Recipe: "রেসিপি",
    fishItem_ExtraInfo: "অতিরিক্ত তথ্য",
    fishItem_IngredientsLabel: "উপকরণ",
    extraInfo_Display_SharedBy: "শেয়ার করেছেন",
    extraInfo_PeopleSharing_Short: "জন",
    extraInfo_Display_ConsumedAt: "খাওয়া হয়েছে",
    extraInfo_Display_Source: "উৎস",
    extraInfo_Display_PreparedBy: "রান্না করেছে",
    extraInfo_Display_CoIngredients: "সাথে",
    fishItem_PortionType: "পরিমাণের ধরন",
    fishItem_PieceType: "টুকরোর ধরন",
    fishItem_NumberOfPortions: "অংশের সংখ্যা",
    fishItem_NumberOfPortions_whole: "মাছের সংখ্যা",
    fishItem_NumberOfPortions_piece: "টুকরোর সংখ্যা",
    fishItem_NumberOfPortions_portion: "বাটির সংখ্যা",
    fishItem_NumberOfPortions_unknown: "অংশের সংখ্যা",
    fishItem_PortionWeight_whole: "প্রতি মাছের ওজন",
    fishItem_PortionWeight_piece: "প্রতি টুকরোর ওজন",
    fishItem_PortionWeight_portion: "প্রতি বাটির ওজন",
    fishItem_PortionWeight_unknown: "প্রতি অংশের ওজন",
    fishItem_TotalQuantity: "মোট পরিমাণ",
    fishItem_ConsumedParts: "খাওয়া অংশ",
    fishItem_Head: "মাথা",
    fishItem_Tail: "লেজ",
    fishItem_SoftBone: "নরম হাড় সহ",
    fishItem_WithoutSoftBone: "নরম হাড় ছাড়া",
    fishItem_Ingredients: "রান্নার উপকরণ",
    fishItem_Edit: "সম্পাদনা করুন",
    fishItem_Delete: "বাদ দিন",

    // Review Fish Item
    reviewFishItem_Heading: "মাছের এন্ট্রি নিশ্চিত করুন",
    reviewFishItem_Missing: "পর্যালোচনার জন্য কোন মাছের আইটেম পাওয়া যায়নি.",
    ReviewFishItem_noFishItem:
      "পর্যালোচনার জন্য কোন মাছের আইটেম পাওয়া যায়নি.",
    ReviewFishItem_noMealRecord:
      "পর্যালোচনার জন্য কোন খাবারের রেকর্ড পাওয়া যায়নি.",
    reviewFishItem_Fish: "মাছ",
    reviewFishItem_Confirm: "খাবারে যোগ করুন",
    reviewFishItem_ConfirmAndFinish: "খাবারে যোগ করুন এবং শেষ করুন",
    reviewFishItem_Edit: "এন্ট্রি সম্পাদনা করুন",
    reviewFishItem_EditWhat: "আপনি কী সম্পাদনা করতে চান?",
    reviewFishItem_EditFish: "মাছের প্রজাতি",
    reviewFishItem_EditRecipe: "রেসিপি",
    reviewFishItem_EditAmount: "পরিমাণ/অংশ",
    reviewFishItem_EditAdditionalInfo: "অতিরিক্ত তথ্য",
    reviewFishItem_CancelEdit: "বাতিল",
    reviewFishItem_Delete: "এন্ট্রি মুছুন",
    reviewFishItem_DeleteItem: "আইটেম বাদ দিন",
    reviewFishItem_ConfirmDelete:
      "আপনি কি নিশ্চিত যে আপনি এই এন্ট্রি মুছতে চান?",
    reviewFishItem_commitEdits: "সম্পাদনা নিশ্চিত করুন",
    reviewFishItem_SaveVerificationFailed: "তথ্য সফলভাবে সংরক্ষিত হয়েছে কিনা তা যাচাই করা যায়নি। খাবারটি রেকর্ড করা হয়েছে কিনা তা নিশ্চিত করতে অনুগ্রহ করে আপনার সাম্প্রতিক লগ পরীক্ষা করুন।",

    // Add Another Fish Item
    addAnotherFishItem_Heading: "আপনি কি আরেকটি মাছের আইটেম যোগ করতে চান?",
    addAnotherFishItem_SameFish: "হ্যাঁ, একই মাছের",
    addAnotherFishItem_DifferentFish: "হ্যাঁ, ভিন্ন মাছ যোগ করুন",
    addAnotherFishItem_Yes: "হ্যাঁ, আরেকটি যোগ করুন",
    addAnotherFishItem_No: "না, এগিয়ে যান",

    // Review log
    reviewLog_Heading: "আপনার খাবারের লগ পর্যালোচনা করুন",
    reviewLog_MissingRecord:
      "পর্যালোচনার জন্য কোন খাবারের রেকর্ড পাওয়া যায়নি।",
    reviewLog_GoBack: "ফিরে যান",
    reviewLog_CloseSurvey: "জরিপ বন্ধ করুন",
    reviewLog_Date: "তারিখ",
    reviewLog_MealType: "খাবারের ধরন",
    reviewLog_TotalItems: "মোট মাছের আইটেম",
    reviewLog_FishItem: "আইটেম",
    reviewLog_pieces: "টুকরা",
    reviewLog_piece: "টুকরা",
    reviewLog_TotalWeight: "মোট ওজন",
    reviewLog_NutritionalValue: "পুষ্টিগুণ",
    reviewLog_Confirm: "ঠিক আছে, সংরক্ষণ করুন",
    reviewLog_Saving: "সংরক্ষণ করা হচ্ছে...",
    reviewLog_AddAnotherFishItem: "আরেকটি যোগ করুন",
    reviewLog_NoFishItemsToSave:
      "সংরক্ষণের জন্য কোন মাছের আইটেম পাওয়া যায়নি।",
    reviewLog_NoFishItemsToSaveConfirmToProceed:
      "আপনি কি নিশ্চিত যে কোন মাছের আইটেম সংরক্ষণ না করেই এগিয়ে যেতে চান?",
    reviewLog_UpdatedAt: "শেষ আপডেট হয়েছে",
    reviewLog_SaveVerificationFailed: "তথ্য সফলভাবে সংরক্ষিত হয়েছে কিনা তা যাচাই করা যায়নি। খাবারটি রেকর্ড করা হয়েছে কিনা তা নিশ্চিত করতে অনুগ্রহ করে আপনার সাম্প্রতিক লগ পরীক্ষা করুন।",

    // Meal Log Session Info
    mealLog_SessionTime: "লগ সেশন সময়",
    mealLog_Minutes: "মিনিট",
    mealLog_Seconds: "সেকেন্ড",
    mealLog_TotalIn: "মোট",
    mealLog_Sessions: "সেশনে",
    mealLog_Session: "সেশন",
    mealLog_FishItems: "মাছের আইটেম",

    // Submitted
    submitted_Heading: "মিল রেকর্ড হয়েছে!",
    submitted_SubText: "আপনার এন্ট্রি সফলভাবে সংরক্ষণ করা হয়েছে।",
    submitted_AddAnotherMeal: "আরেকটি মিল যোগ করুন",
    submitted_Finish: "এখানেই শেষ করুন",
    submitted_AddAnotherDay: "আরেক দিনের রেকর্ড যোগ করুন",

    // Abort Log
    abortLog_Heading: "লগিং সেশন শেষ করবেন?",
    abortLog_SubText: "আপনি কি নিশ্চিত যে আপনি এই লগিং সেশন শেষ করতে চান? কোনো তথ্য সংরক্ষণ করা হবে না।",
    abortLog_YesEnd: "হ্যাঁ, সেশন শেষ করুন",
    abortLog_GoBack: "ফিরে যান",

    // Add Another Day
    addAnotherDay_Heading: "আপনি কি আরেকটি দিন লগ করতে চান?",
    addAnotherDay_Yes: "হ্যাঁ",
    addAnotherDay_No: "না",

    // Log history
    backToPersonList: "জরিপকৃতদের তালিকায় ফিরে যান",
    loadMore: "আরও লোড করুন",
    logHistory_Heading: "{name} এর মাছ খাওয়ার লগ",
    logHistoryDateLog_NoRecord: "এই তারিখের জন্য কোন লগ রেকর্ড পাওয়া যায়নি।",
    logHistoryDateLog_NoFishConsumed: "এই দিন কোন মাছ খায়নি",
    logHistoryDateLog_NoPerson: "ব্যক্তি পাওয়া যায়নি",
    logHistoryDateLog_Heading: "{date} এর লগ",
    logHistoryDateLog_totalNutrientIntakeHeader: "মোট পুষ্টি গ্রহণ",
    logHistoryDateLog_totalNutrientIntakeSubheader:
      "(দৈনিক প্রস্তাবিত পরিমাণের ভিত্তিতে)",
    logHistoryDateLog_totalConsumedWeightHeader: "মোট গৃহীত মাছের ওজন",
    logHistoryDateLog_Back: "ক্যালেন্ডার ভিউতে ফিরে যান",    logHistoryDateLog_BackToCalendar: "ক্যালেন্ডার ভিউতে ফিরে যান",
    logHistoryDateLog_BackToAllLogs: "লগ তালিকায় ফিরে যান",
    logHistoryDateLog_BackToSurvey: "সার্ভে ড্যাশবোর্ডে ফিরে যান",    logHistoryDateLog_AddNewMeal: "নতুন মিল যোগ করুন",
    saving: "সংরক্ষণ করা হচ্ছে...",
    logHistoryDateLog_DeleteFishItemConfirm:
      "আপনি কি নিশ্চিত যে আপনি এই মাছের আইটেম মুছতে চান?",
    logHistoryDateLog_NoMealsLeft:
      "এই দিনে কোন খাবারের আইটেম অবশিষ্ট নেই। দিনের রেকর্ড মুছে ফেলা হয়েছে।",
    logHistoryDateLog_FishItemRemoved: "মাছের আইটেম মুছে ফেলা হয়েছে।",
    logHistory_goToCurrentMonth: "বর্তমান মাসে যান",
    logHistory_monthlySummary: "মাসিক সারাংশ",
    
    // Monthly Stats
    monthlyStats_heading: "মাসিক সারাংশ",
    monthlyStats_daysWithLogs: "লগ সহ দিন",
    monthlyStats_totalMeals: "মোট খাবার",
    monthlyStats_totalFishWeight: "মোট মাছ খাওয়া হয়েছে",
    monthlyStats_FishConsumed: "গৃহীত মাছ",
    monthlyStats_fishBySpecies: "প্রজাতি অনুসারে মাছ",
    monthlyStats_times: "বার",
    monthlyStats_totalNutrients: "মোট পুষ্টি গ্রহণ",
    monthlyStats_ofDRI: "DRI এর",
    monthlyStats_note: "নোট",
    monthlyStats_driNote: "DRI (দৈনিক রেফারেন্স গ্রহণ) শতাংশ দৈনিক গড় দৃশ্যে প্রদর্শিত হয় প্রাপ্তবয়স্কদের দৈনিক পুষ্টি গ্রহণের সুপারিশকৃত মানের সাথে তুলনা করার জন্য।",
    monthlyStats_excludedNote: "পুষ্টির মোট হিসাব থেকে পুষ্টি তথ্য নেই এমন এন্ট্রিগুলো (যেমন \"অন্যান্য\" মাছ) বাদ দেওয়া হয়েছে।",

    //Nutrients:
    protein: "প্রোটিন",
    fat: "চর্বি",
    energy: "শক্তি",
    carbohydrates: "কার্বোহাইড্রেট",
    calories: "ক্যালোরি",
    omega3: "ওমেগা-৩",
    omega6: "ওমেগা-৬",
    calcium: "ক্যালসিয়াম",
    iron: "আয়রন",
    zinc: "জিঙ্ক",
    magnesium: "ম্যাগনেসিয়াম",
    phosphorus: "ফসফরাস",
    potassium: "পটাসিয়াম",
    sodium: "সোডিয়াম",
    iodine: "আয়োডিন",
    copper: "তামা",
    manganese: "ম্যাঙ্গানিজ",
    selenium: "সেলেনিয়াম",
    sulphur: "সালফার",
    vitaminA: "ভিটামিন এ",
    vitaminD: "ভিটামিন ডি",
    vitaminE: "ভিটামিন ই",
    thiamin: "থায়ামিন (বি১)",
    riboflavin: "রিবোফ্লাভিন (বি২)",
    niacin: "নায়াসিন (বি৩)",
    vitaminB6: "ভিটামিন বি৬",
    folate: "ফোলেট (বি৯)",
    vitaminB12: "ভিটামিন বি১২",
    showMoreNutrients: "+ আরও {count}টি",
    showLessNutrients: "কম দেখান",
  },
};
